import { ErrorMessage, Field } from 'formik';
import { Col, Form, FormControl, Row, Stack } from 'react-bootstrap';
import Editor from '../../../../../../components/editor/Editor';
import { FormLabel, FormStepHeader } from '../../../../../../components/forms';
import { useGetJokesQuery } from '../../../../../../services/endpoints/content/joke';
import { useGetJokeCategoriesQuery } from '../../../../../../services/endpoints/content/jokeCategory';
import { Joke, JokeCategory } from '../../../../../../types/content.type';
import { StepProps } from '../AddEditRecipe';
import React, {Fragment} from "react";
import {Menu, MenuItem, Typeahead} from "react-bootstrap-typeahead";

export const RecipeListsForm = ({
  touched,
  errors,
  values,
  setFieldValue,
  readOnly
}: StepProps) => {
  const [jokesOpen, setJokesOpen] = React.useState(false);
  const { data: dataJokeCategories, error: errorJokeCategories } = useGetJokeCategoriesQuery({data: {entries: 9999}});
  const { data: dataJokes, error: errorJokes } = useGetJokesQuery({data: {entries: 9999}});
  return (
    <Row>
      <Col xs={12}>
        <FormStepHeader title="Recipe Lists" showRequiredLegend={!readOnly} />
      </Col>
      <Col
        xs={12}
        lg={6}
      >
        <Stack gap={3}>
          <Form.Group controlId="ingredients">
            <FormLabel>Ingredients</FormLabel>
            <Field
              name="ingredients"
              as="textarea"
              className="form-control"
              rows={4}
              isInvalid={touched.ingredients && !!errors.ingredients}
            />
            <Form.Text>
              <p>
                This will be broken up based on line breaks. Place scaled items in double curly
                brackets. <br />
                Example:
                <br />
                &#123;&#123;1&#125;&#125; carrot
                <br />
                &#123;&#123;3&#125;&#125; oz. milk
              </p>
            </Form.Text>
          </Form.Group>
          <Form.Group controlId="substitutions">
            <FormLabel>Food Allergen Substitutions</FormLabel>
            <Field
              name="substitutions"
              component={Editor}
            />
          </Form.Group>
          <Form.Group controlId="recipe_joke_ids">
            <FormLabel required>Jokes</FormLabel>
            <Typeahead
                id="recipe_joke_ids"
                multiple
                labelKey="name"
                disabled={readOnly}
                placeholder="Select jokes"
                maxResults={1000}
                flip
                positionFixed
                isInvalid={touched.recipe_joke_ids && !!errors.recipe_joke_ids}
                open={jokesOpen}
                onFocus={() => setJokesOpen(true)}
                onBlur={() => setJokesOpen(false)}
                options={dataJokes?.results ?? []}
                selected={(dataJokes?.results ?? []).filter((joke: Joke) =>
                    values.recipe_joke_ids?.includes(joke.id)
                )}
                onChange={(selected) =>
                    setFieldValue?.(
                        'recipe_joke_ids',
                        (selected as Joke[]).map((joke) => joke.id)
                    )
                }
                renderMenu={(results, menuProps) => {
                  let index = 0;
                  const sections = (dataJokeCategories?.results ?? [])
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((category: JokeCategory) => {
                        const jokesInCategory = (results as Joke[])
                            .filter((joke) => joke.joke_category_id === category.id)
                            .sort((a, b) => a.name.localeCompare(b.name));
                        if (jokesInCategory.length === 0) return null;
                        const section = (
                            <Fragment key={category.id}>
                              {index !== 0 && <Menu.Divider />}
                              <Menu.Header>{category.name}</Menu.Header>
                              {jokesInCategory.map((joke) => {
                                const item = (
                                    <MenuItem key={joke.id} option={joke} position={index}>
                                      {joke.name}
                                    </MenuItem>
                                );
                                index += 1;
                                return item;
                              })}
                            </Fragment>
                        );
                        return section;
                      });
                  return <Menu {...menuProps}>{sections}</Menu>;
                }}
            />
            <ErrorMessage name="recipe_joke_ids">
              {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
            </ErrorMessage>
          </Form.Group>
          <Form.Group controlId="parent_equipments">
            <FormLabel>Parent Equipments</FormLabel>
            <Field
              name="parent_equipments"
              as="textarea"
              className="form-control"
              rows={4}
              isInvalid={touched.parent_equipments && !!errors.parent_equipments}
            />
            <Form.Text>
              <p>
                This will be broken up based on line breaks. Example:
                <br />
                Toaster oven
                <br />
                Parchment paper
              </p>
            </Form.Text>
          </Form.Group>
          <Form.Group controlId="instructor_equipments">
            <FormLabel>Instructor Equipments</FormLabel>
            <Field
              name="instructor_equipments"
              as="textarea"
              className="form-control"
              rows={4}
              isInvalid={touched.instructor_equipments && !!errors.instructor_equipments}
            />
            <Form.Text>
              <p>
                This will be broken up based on line breaks. Example:
                <br />
                Toaster oven
                <br />
                Parchment paper
              </p>
            </Form.Text>
          </Form.Group>
        </Stack>
      </Col>
    </Row>
  );
};
